import { useCallback, useEffect, useState } from 'react';
import { notify } from '../notify';

/**
 * Departments and designations are the same screen with different columns, so
 * they share one field-driven component rather than two near-identical files.
 *
 * fields: [{ key, label, type, required, placeholder }]
 */
export default function LookupAdmin({ title, subtitle, resource, fields, canDelete, prefill }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const blank = useCallback(() => {
    const f = { active: true };
    fields.forEach((x) => (f[x.key] = ''));
    return f;
  }, [fields]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await resource.list()) || []);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { active: !!form.active };
      fields.forEach(({ key, type }) => {
        const v = form[key];
        if (v === '' || v === null || v === undefined) return;
        payload[key] = type === 'number' ? Number(v) : v;
      });

      if (editing === 'new') {
        await resource.create(payload);
        notify.success('Created');
      } else {
        await resource.update(editing, payload);
        notify.success('Updated');
      }
      setEditing(null);
      setForm(blank());
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function act(fn, okMsg) {
    try {
      await fn();
      notify.success(okMsg);
      await load();
    } catch (err) {
      notify.error(err.message);
    }
  }

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>{title}</h2>
          <p className="muted">{subtitle}</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setForm(blank());
            setEditing('new');
            // Optional server-side defaults, e.g. the next free code. Failure
            // is harmless: the server fills blanks on save anyway.
            if (prefill) {
              prefill().then((v) => setForm((f) => ({ ...f, ...v }))).catch(() => {});
            }
          }}
        >
          + Add
        </button>
      </div>

      {editing !== null && (
        <form className="card form-grid" onSubmit={save}>
          <h3 className="span-all">{editing === 'new' ? `New ${title.toLowerCase()}` : `Edit #${editing}`}</h3>
          {fields.map((f) => (
            <label className={`field ${f.wide ? 'span-all' : ''}`} key={f.key}>
              <span>{f.label}{f.required ? ' *' : ''}</span>
              {f.hint && editing === 'new' && <span className="small muted">{f.hint}</span>}
              {f.type === 'textarea' ? (
                <textarea rows={2} value={form[f.key] ?? ''} onChange={set(f.key)} />
              ) : (
                <input
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={form[f.key] ?? ''}
                  onChange={set(f.key)}
                  placeholder={f.placeholder}
                  required={f.required}
                />
              )}
            </label>
          ))}
          <label className="check span-all">
            <input type="checkbox" checked={!!form.active} onChange={set('active')} />
            Active
          </label>
          <div className="span-all actions">
            <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card empty">
          <p><strong>Nothing here yet.</strong></p>
          <p className="muted">Add the first {title.toLowerCase().replace(/s$/, '')}.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                {fields.map((f) => <th key={f.key}>{f.label}</th>)}
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.active ? '' : 'exited'}>
                  <td className="mono">{r.id}</td>
                  {fields.map((f) => (
                    <td key={f.key} className={f.key === 'description' ? 'small' : ''}>
                      {r[f.key] === null || r[f.key] === undefined || r[f.key] === '' ? '—' : String(r[f.key])}
                    </td>
                  ))}
                  <td>
                    <span className={`pill pill-${r.active ? 'active' : 'exited'}`}>
                      {r.active ? 'YES' : 'NO'}
                    </span>
                  </td>
                  <td className="nowrap">
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        setForm({ ...blank(), ...r });
                        setEditing(r.id);
                      }}
                    >
                      Edit
                    </button>
                    {r.active && (
                      <button
                        className="btn btn-sm"
                        onClick={() => act(() => resource.deactivate(r.id), 'Deactivated')}
                      >
                        Deactivate
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => act(() => resource.del(r.id), 'Deleted')}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
